import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { makeStyles, Popover } from "@material-ui/core";
import { connect } from "react-redux";

import {
  AppModal,
  AppAlert,
  AppButton,
  AppIcon,
  MotifAppSelect,
  MotifMultiAppSelect,
  AppTooltip,
} from "../../theme";
import { exclamation, info, xMark } from "../../theme/icons";
import cloudUpload from "../../assets/images/icons/cloud-upload.svg";
import checkCircle from "../../assets/images/icons/check_circle.svg";
import t from "../../localization/en/translation.json";
import {
  getProjectEngagement,
  fetchDbsFields,
} from "../../store/apis/projectApi";
import { DropZoneErrorStatuses, ProjectDbsFolderPath } from "../../utils/Constants";
import CloseProjectConfirmationPopup from "../closeProjectConfirmationPopup/CloseProjectConfirmationPopup";
import themeConfig from "../../theme/config";
import Loader from "../loader/loader";
import { getProjectTypesLookUpAction } from "../../store/actions/project-types-actions";
import { CountryLookupModel, lookupModel } from "../../containers/createNewProject/lookupModel";
import { Project } from "../createProject/models/project";
import { EngagementIdServiceTicketEndUrl } from "../../utils/Constants";
import { getCountriesCodeAction } from "../../store/actions/data-locations-actions";

import "./CloseProjectPopup.scss";

export type FeedbackType = {
  engagementIds?: string[];
  engagementFiles?: { name: string, blobName: string }[];
  cicClause?: string;
  hasRestrictiveClauses?: string;
  hasThirdPartyRestrictiveClauses?: string;
  purchaseSuccess?: string;
  hasInsiderList?: string;
};

export type DbsType = {
  engagementIds?: string[];
  engagementFiles?: { name: string, blobName: string }[];
  cicClause?: string;
  hasRestrictiveClauses?: string;
  hasThirdPartyRestrictiveClauses?: string;
  purchaseSuccess?: string;
  transmissionStatus?: string | null;
  hasInsiderList?: string;
  countryName: string;
  countryId: string;
  paceId: string;
  projetName: string;
  projectCategoryId: string;
  projectCategoryName: string;
};

export const ConfirmCICFeedback = ({
  projectId,
  paceId,
  uploadedFile,
  onFeedback,
  addProjectDocument,
  deleteProjectDocument,
  flushProjectUploadedFile,
  isPortalAdmin,
  projectTypes,
  getProjectTypes,
  dbsDetails = null,
  projectCountries
}: {
  projectId: string;
  paceId: string | null | undefined;
  uploadedFile: string;
  onFeedback: (feedback: FeedbackType) => void;
  addProjectDocument(payload: any): any;
  deleteProjectDocument(payload: any): any;
  flushProjectUploadedFile(): any;
  isPortalAdmin: boolean;
  projectTypes: lookupModel[];
  getProjectTypes(payload: any): void;
  dbsDetails?: DbsType | null;
  projectCountries: CountryLookupModel[];
}) => {

  const [engagements, setEngagements] = useState<{ label: string; value: string; isDisabled?: boolean}[]>([]);
  const [isEngagementsEmpty, setIsEngagementsEmpty] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [engagementIds, setEngagementIds] = useState<string[]>([]);
  const [documentFiles, setDocumentFiles] = useState<{ name: string; blobName: string }[]>([]);
  const [addedFileName, setAddedFileName] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [successfulPurchaser, setSuccessfulPurchaser] = useState<string>("");
  const [restrictiveClause, setRestrictiveClause] = useState<string>("");
  const [thirdPartyRestrictiveClause, setThirdPartyRestrictiveClause] = useState<string>("");
  const [hasInsiderList, setHasInsiderList] = useState<string>("");
  const [dbsFields, setDbsFields] = useState([]);
  const [showDBSInfo, setShowDBSInfo] = useState<boolean>(false);
  const [showInsiderList, setShowInsiderList] = useState<boolean>(false);

  const engagementTicketUrl = process.env.REACT_APP_HELP_CAPITAL_EDGE_URL?.split("=")[0] + EngagementIdServiceTicketEndUrl;
  const cicValue = "Yes";

  const useStyles = makeStyles((theme) => ({
    popover: {
      padding: theme.spacing(1.4),
      fontSize: themeConfig.fontSize14,
      background: themeConfig.black,
      color: themeConfig.white
    },
    serviceLink: {
      color: themeConfig.link_blue,
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  }));

  const classes = useStyles();
  const openInfoPopOver = Boolean(anchorEl);
  const handleInfoButtonClick = (event: React.MouseEvent<HTMLElement>) => { setAnchorEl(event.currentTarget); };
  const handleClose = () => { setAnchorEl(null); };

  const handleEngagementChange = ({
    target: { value },
  }: {
    target: { value: string[] };
  }) => {
    setEngagementIds(value);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const formData = new FormData();
    acceptedFiles.forEach((acceptedFile) => {
      formData.append("file", acceptedFile);
    });
    formData.append("folderPath", ProjectDbsFolderPath);
    if (acceptedFiles.length > 0) {
      addProjectDocument({ projectId, formData });
      setAddedFileName(acceptedFiles[0].name);
    }
    // eslint-disable-next-line
  }, []);

  const onDragOver = (e) => {
    const event = e as Event;
    event.stopPropagation();
    event.preventDefault();
  };

  const onDragEnter = (e) => {
    const event = e as Event;
    event.stopPropagation();
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: [".pdf", ".doc", ".docx", ".ppt"],
    maxFiles: 1,
    maxSize: 26214400,
    onDrop,
    onDragOver,
    onDragEnter,
    multiple: false,
    noDragEventsBubbling: true,
  });

  const renderFileUploadError = () => {
    const [
      {
        errors: [{ code }],
      },
    ] = fileRejections;

    if (code === DropZoneErrorStatuses.FILE_TOO_LARGE) {
      return t.engagement_document_upload_file_size_validation_message;
    }

    if (code === DropZoneErrorStatuses.FILE_INVALID_TYPE) {
      return t.engagement_document_upload_file_type_validation_message;
    }

    return t.engagement_document_upload_validation_message;
  };

  const handleDeleteDocument = (blobName: string) => {
    deleteProjectDocument({ projectId, blobName, folderPath: ProjectDbsFolderPath });
    setDocumentFiles((current) => current.filter((file) => file.blobName !== blobName));
  };

  const getDbsFieldDetails = (key: string) => {
    return dbsFields.find((field) => field.key === key);
  }

  const isInsiderListAvailable = useCallback((): boolean => {
    const InsiderListAvailableCountries = projectCountries.filter(country => country.key === 'unitedkingdom' || country.key === 'ireland');
    const isInsiderCountryAvailable = InsiderListAvailableCountries.some(country => country.id === dbsDetails?.countryId);
    return isInsiderCountryAvailable;
  }, [dbsDetails, projectCountries]);

  const getDBSData = useCallback(() => {
    if (dbsDetails) {
      setSuccessfulPurchaser(dbsDetails.purchaseSuccess || '');
      setRestrictiveClause(dbsDetails.hasRestrictiveClauses || '');
      setThirdPartyRestrictiveClause(dbsDetails.hasThirdPartyRestrictiveClauses || '');
      setDocumentFiles(dbsDetails.engagementFiles || []);
      setEngagementIds(dbsDetails.engagementIds || []);
      setHasInsiderList(dbsDetails.hasInsiderList || '');
    }
  }, [dbsDetails])

  useEffect(() => {
    getDBSData();
  }, [getDBSData]);

  useEffect(() => {
    (async () => {
      try {
        setShowLoader(true);
        const dbsFieldsResponse = await fetchDbsFields();
        if (dbsFieldsResponse && dbsFieldsResponse?.data?.dbs) {
          setDbsFields(dbsFieldsResponse.data.dbs)
        }
        setShowLoader(false);
        const engagementResponse = await getProjectEngagement(paceId);
        const engagementData = engagementResponse?.data as {
          id: string;
          name: string;
        }[];
        const engagementEmptyOptions = {label: t.engagement_id_not_in_the_list, value: t.engagement_id_not_in_the_list, isDisabled:false}
        const engagementOptions = [...engagementData.map((option) => ({ label: option.id, value: option.id, isDisabled: false })), engagementEmptyOptions]
        setEngagements(engagementOptions);

        if (!engagementData.length)
          setIsEngagementsEmpty(true);
      } catch { }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setShowInsiderList(isInsiderListAvailable());
    if (projectTypes && !projectTypes.length) {
      getProjectTypes({ loading: false });
    }
  }, [projectTypes, getProjectTypes, isInsiderListAvailable])

  useEffect(() => {
    const feedbackData: FeedbackType = {
      engagementIds: engagementIds,
      engagementFiles: documentFiles.map(file => {
        return {
          name: file.name,
          blobName: file.blobName
        };
      }),
      cicClause: cicValue,
      purchaseSuccess: successfulPurchaser,
      hasRestrictiveClauses: restrictiveClause,
      hasThirdPartyRestrictiveClauses: thirdPartyRestrictiveClause
    };

    if (showInsiderList)
      feedbackData.hasInsiderList = hasInsiderList;

    onFeedback(feedbackData);
    if (uploadedFile && uploadedFile !== "" && addedFileName !== "") {
      setDocumentFiles((current) => [
        ...current,
        { name: addedFileName, blobName: uploadedFile },
      ]);
      setAddedFileName("");
      flushProjectUploadedFile();
    }
    // eslint-disable-next-line
  }, [
    engagementIds,
    onFeedback,
    uploadedFile,
    addedFileName,
    documentFiles,
    cicValue,
    restrictiveClause,
    thirdPartyRestrictiveClause,
    successfulPurchaser,
    hasInsiderList
  ]);

  const notInList = [t.engagement_id_not_in_the_list]
  const handleDisableEngagements = (selected:string[], option: {label: string; value:string | number; isDisabled?:boolean})=> {
    if(selected.some((val) => notInList.includes(val)) && !notInList.includes(String(option.value))){
      return true;
    }
    if(selected.some(val => !notInList.includes(val)) && notInList.includes(String(option.value))){
      return true;
    }

    return option.isDisabled || false;
  }

  return (
    <>
      <Loader isLoading={showLoader} isFullSize={false} />
      <div className={"close-project-feedback cic-feedback"}>
        <AppAlert severity="info" className="info-alert-custom">
          {t.data_bureau_service_message}
          {showDBSInfo &&
            <div className="dbs-info-content">
              {t.dbs_info_content_line_1}
              <br /><br />
              {t.dbs_info_content_line_2}
              <br /><br />
              {t.dbs_info_content_line_3}
              <br /><br />
              {t.dbs_info_content_line_4}
            </div>
          }
          <div className="link" onClick={() => setShowDBSInfo(!showDBSInfo)}>{showDBSInfo ? 'Show less' : 'Show more'}</div>
        </AppAlert>
        <div className={"file-upload-wrapper"}>
          <span className="input-label">
            {t.engagement_document_upload_title}
            <span className="asterisk">*</span>
          </span>
          <div
            className={
              !!fileRejections.length
                ? "drag-drop-wrapper-alert"
                : "drag-drop-wrapper"
            }
            style={{ pointerEvents: documentFiles.length >= 5 ? "none" : "auto" }}
          >
            <div {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} />
              {!!fileRejections.length ? (
                <AppIcon icon={exclamation} />
              ) : (
                <img
                  src={cloudUpload}
                  className="upload-icon-size"
                  alt="Upload"
                />
              )}
              <span className="upload-text">
                {!!fileRejections.length
                  ? renderFileUploadError()
                  : t.engagement_document_upload_area_title_1}
              </span>
              <span className="upload-options">
                {t.engagement_document_upload_area_title_2}
              </span>
            </div>
          </div>
          {documentFiles !== undefined && documentFiles.length > 0 && (
            <div className={"file-list"}>
              {documentFiles.map((file, i) => (
                <div
                  key={i+file.name}
                  className={"file"}
                  onMouseEnter={() => setHoveredFile(file.name)}
                  onMouseLeave={() => setHoveredFile(null)}
                >
                  <span className={"filename"} title={i+file.name}>{file.name}</span>
                  <AppButton
                    className={"file-icon-cross"}
                    variant="text"
                    onClick={() => handleDeleteDocument(file.blobName)}
                  >
                    {hoveredFile === file.name ? (
                      <AppIcon icon={xMark} className="Close-circleCheck" />
                    ) : (
                      <img
                        src={checkCircle}
                        className="Close-circleCheck"
                        alt=""
                      />
                    )}
                  </AppButton>
                </div>
              ))}
            </div>
          )}
        </div>
        {!showLoader &&
          <>
            <div className="row">
              <div className="question">
                <span className="input-label">
                  {t.engagement_id}
                  <span className="asterisk">*</span>
                  {isEngagementsEmpty &&
                    <div className="no-engagement-info-msg">
                      <Popover
                        open={openInfoPopOver}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                        style={{ maxWidth: '60%' }}
                      >
                        <div className={classes.popover}>
                          {t.no_engagement_id_info_message_line_1}
                          <span onClick={() => window.open(`${engagementTicketUrl}`, '_blank')} className={classes.serviceLink}>
                            {t.service_request_link}
                          </span>
                          {t.no_engagement_id_info_message_line_2}
                        </div>
                      </Popover>
                      <AppIcon icon={info} onClick={handleInfoButtonClick} />
                    </div>
                  }
                </span>
              </div>
              <div className="feedback-input">
                <MotifMultiAppSelect
                  value={engagementIds || []}
                  isRequired={false}
                  multiple={true}
                  onChange={handleEngagementChange}
                  displayEmpty={true}
                  emptyValue={t.no_items_found}
                  options={engagements || []}
                  disableItems={handleDisableEngagements}
                />
              </div>
            </div>
            {dbsFields && !!dbsFields.length &&
              <>

                <div className="row">
                  <div className="question">
                    <span className="input-label">
                      {getDbsFieldDetails('PRCSUCCESS')?.title}
                    </span>
                    <span className="asterisk">*</span>
                    <AppTooltip title={getDbsFieldDetails('PRCSUCCESS')?.description || ''}>
                      <AppIcon icon={info} style={{ fontSize: 14 }} />
                    </AppTooltip>
                  </div>
                  <div className="feedback-input">
                    <MotifAppSelect
                      value={successfulPurchaser}
                      isRequired={false}
                      onChange={(e: any) => { setSuccessfulPurchaser(e.target.value) }}
                      displayEmpty={true}
                      emptyValue={t.no_items_found}
                      options={getDbsFieldDetails('PRCSUCCESS')?.values.map((value) => ({ value: value, label: value })) || []}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="question">
                    <span className="input-label">
                      {getDbsFieldDetails('HASRSTRCTCLS')?.title}
                    </span>
                    <span className="asterisk">*</span>
                    <AppTooltip title={getDbsFieldDetails('HASRSTRCTCLS')?.description || ''}>
                      <AppIcon icon={info} style={{ fontSize: 14 }} />
                    </AppTooltip>
                  </div>
                  <div className={"feedback-input"}>
                    <MotifAppSelect
                      value={restrictiveClause}
                      isRequired={false}
                      onChange={(e: any) => { setRestrictiveClause(e.target?.value) }}
                      displayEmpty={true}
                      emptyValue={t.no_items_found}
                      options={getDbsFieldDetails('HASRSTRCTCLS')?.values.map((value) => ({ value: value, label: value })) || []}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="question">
                    <span className="input-label">
                      {getDbsFieldDetails('THIRDPARTYRSTRCL')?.title}
                    </span>
                    <span className="asterisk">*</span>
                    <AppTooltip title={getDbsFieldDetails('THIRDPARTYRSTRCL')?.description || ''}>
                      <AppIcon icon={info} style={{ fontSize: 14 }} />
                    </AppTooltip>
                  </div>
                  <div className={"feedback-input"}>
                    <MotifAppSelect
                      value={thirdPartyRestrictiveClause}
                      isRequired={false}
                      onChange={(e: any) => { setThirdPartyRestrictiveClause(e.target.value) }}
                      displayEmpty={true}
                      emptyValue={t.no_items_found}
                      options={getDbsFieldDetails('THIRDPARTYRSTRCL')?.values.map((value) => ({ value: value, label: value })) || []}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="question">
                    <span>
                      <span className="input-label">
                        {getDbsFieldDetails('CICClause')?.title}
                      </span>
                      <span className="asterisk">*</span>
                      <AppTooltip title={getDbsFieldDetails('CICClause')?.description || ''}>
                        <AppIcon icon={info} style={{ fontSize: 14 }} />
                      </AppTooltip>
                    </span>
                  </div>
                  <div className="feedback-input">
                    <div className="cic-status">{t.cic_status_label}</div>
                  </div>
                </div>
                {showInsiderList &&
                  <div className="row">
                    <div className="question">
                      <span>
                        <span className="input-label">
                          {getDbsFieldDetails('INSIDERLIST')?.title}
                        </span>
                        <span className="asterisk">*</span>
                        <AppTooltip title={getDbsFieldDetails('INSIDERLIST')?.description || ''}>
                          <AppIcon icon={info} style={{ fontSize: 14 }} />
                        </AppTooltip>
                      </span>
                    </div>
                    <div className="feedback-input">
                      <MotifAppSelect
                        value={hasInsiderList}
                        isRequired={false}
                        onChange={(e: any) => { setHasInsiderList(e.target.value) }}
                        displayEmpty={true}
                        emptyValue={t.no_items_found}
                        options={getDbsFieldDetails('INSIDERLIST')?.values.map((value) => ({ value: value, label: value })) || []}
                      />
                    </div>
                  </div>
                }
              </>
            }
          </>
        }
      </div>
    </>
  );
};

const ConfirmCICPopup = ({
  show,
  onClosePopup,
  isPricingFeedback,
  onProjectClose,
  projectId,
  projectName,
  paceId,
  uploadedFile,
  addProjectDocument,
  deleteProjectDocument,
  flushProjectUploadedFile,
  isPortalAdmin,
  projectTypes,
  getProjectTypes,
  projectDetails = null,
  dbsDetails = null,
  getCountries,
  projectCountries
}: {
  show: boolean;
  onClosePopup: () => void;
  isPricingFeedback: boolean;
  onProjectClose: (feedback: FeedbackType) => void;
  projectId: string;
  projectName: string;
  paceId: string | null | undefined;
  uploadedFile: string;
  addProjectDocument;
  deleteProjectDocument;
  flushProjectUploadedFile;
  isPortalAdmin: boolean;
  projectTypes: lookupModel[];
  getProjectTypes(): void;
  projectDetails?: Project | null;
  dbsDetails?: DbsType | null;
  getCountries(): void;
  projectCountries: CountryLookupModel[];
}) => {
  const [feedback, setFeedback] = useState<FeedbackType>({});

  const handleClose = useCallback(() => {
    feedback.engagementIds = feedback.engagementIds?.includes(t.engagement_id_not_in_the_list) ? []:feedback.engagementIds;
    onProjectClose(feedback);
    onClosePopup();
  }, [feedback, onClosePopup, onProjectClose]);

  const onFeedback = useCallback(
    (feedback: FeedbackType) => setFeedback(feedback),
    [setFeedback]
  );

  const areDBSMandatoryFieldsFilled = (
    feedback.engagementIds != null &&
    feedback.engagementIds.length > 0 &&
    feedback.engagementFiles != null &&
    feedback.engagementFiles.length > 0 &&
    feedback.hasRestrictiveClauses?.length &&
    feedback.hasThirdPartyRestrictiveClauses?.length &&
    feedback.cicClause?.length &&
    feedback.purchaseSuccess?.length
  )

  let areMandatoryConditionalFieldsFilled = false
  areMandatoryConditionalFieldsFilled = 
    ('hasInsiderList' in feedback ? Boolean(feedback.hasInsiderList?.length) : true)

  const canConfirmWithMandatoryFields = isPricingFeedback ? areDBSMandatoryFieldsFilled && areMandatoryConditionalFieldsFilled : false;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCicWarning, setShowCicWarning] = useState(false);
  const handleConfirmationClose = () => {
    setShowConfirmation(false);
  };
  const handleConfirmationConfirm = () => {
    setShowConfirmation(false);
    handleClose();
  };

  const handleCicWarning = () => {
    if (isPricingFeedback && feedback.cicClause === 'No') {
      setShowCicWarning(true)
    } else {
      handleConfirmPopUpClose();
    }
  };

  const handleConfirmPopUpClose = () => {
    setShowCicWarning(false);
    isPricingFeedback ? setShowConfirmation(true) : handleClose();
  };

  const handleConfirmationCloseModel = () => {
    setShowConfirmation(false);
    onClosePopup();
  };

  useEffect(() => {
    setShowConfirmation(!isPricingFeedback && show);

    if (!projectCountries.length)
      getCountries();
  }, [show, isPricingFeedback, getCountries, projectCountries]);

  return (
    <>
      {!isPricingFeedback ? (
        <CloseProjectConfirmationPopup
          show={showConfirmation}
          onClosePopup={handleConfirmationCloseModel}
          handleClose={handleConfirmationConfirm}
        />
      ) : (
        <>
          <AppModal
            newDesign={true}
            showModal={show}
            onModalClose={() => onClosePopup()}
            onConfirm={() => handleCicWarning()}
            hideFooterCancelBtn={true}
            confirmBtnText={t.confirm}
            disabledValue={!canConfirmWithMandatoryFields}
            title={projectName + ': ' + t.confirm_cic_modal_title}
            size="lg"
            fullWidth={false}
            modalClass={"custom-engagement-close-project"}
          >
            <div className="motif-close-project modal-content">
              <ConfirmCICFeedback
                onFeedback={onFeedback}
                paceId={paceId}
                projectId={projectId}
                uploadedFile={uploadedFile}
                addProjectDocument={addProjectDocument}
                deleteProjectDocument={deleteProjectDocument}
                flushProjectUploadedFile={flushProjectUploadedFile}
                isPortalAdmin={isPortalAdmin}
                projectTypes={projectTypes}
                getProjectTypes={getProjectTypes}
                dbsDetails={dbsDetails}
                projectCountries={projectCountries}
              />
            </div>
          </AppModal>
          <AppModal
            newDesign={true}
            showModal={showCicWarning}
            onModalClose={() => handleConfirmPopUpClose()}
            onConfirm={() => handleConfirmPopUpClose()}
            hideFooterCancelBtn={true}
            confirmBtnText={t.ok}
            size="sm"
            fullWidth={false}
            showCloseModelIcon={false}
            modalClass="cic-clause-warning"
          >
            <div className="modal-content">
              {t.cic_clause_warning_msg}
            </div>
          </AppModal>
          <CloseProjectConfirmationPopup
            show={showConfirmation}
            onClosePopup={handleConfirmationClose}
            handleClose={handleConfirmationConfirm}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = ({
  projectTypesReducer,
  dataLocationsReducer
}) => {
  return {
    projectTypes: projectTypesReducer.projectTypes,
    projectCountries: dataLocationsReducer.projectCountries,
  };
};

const mapDisptachToProps = ({
  getProjectTypes: (payload) => getProjectTypesLookUpAction(payload),
  getCountries: () => getCountriesCodeAction()
});

export default connect(mapStateToProps, mapDisptachToProps)(ConfirmCICPopup);
